// frontend/src/modules/esign/pages/PrepareWizardPage.tsx
//
// The 4-step "prepare and send" wizard: upload → recipients → field editor
// → review/send. One page component handles both routes:
//   - `/dashboard/esign/new`          — step 1 (upload), no `:id` yet.
//   - `/dashboard/esign/:id/prepare`  — steps 2-4, resuming a draft.
// Uploading creates the document then navigates to the `:id/prepare` route,
// so there's a single source of truth for "load this draft" (the effect
// below) rather than two divergent code paths.
import * as React from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { UploadCloud, Loader2 } from 'lucide-react';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { useConfirm } from '../../../components/ui/confirm-dialog';
import { cn } from '../../../lib/utils';
import { esignApi } from '../api';
import { loadPdf, type PDFDocumentProxy } from '../pdf';
import type { EsignDocument, EsignRecipientRole, EsignSigningOrder } from '../types';
import {
  RecipientsEditor,
  recipientsAreValid,
  makeLocalId,
  type DraftRecipient,
  type PickableEmployee,
} from '../components/RecipientsEditor';
import { FieldEditor, everySignerHasSignatureField, type DraftField } from '../components/FieldEditor';

type WizardStep = 1 | 2 | 3 | 4;

/** Content fingerprint of a recipients array + signing order, ignoring
 *  server-assigned `id` (which changes on every persist) and localId (which
 *  is purely a client bookkeeping detail) — so two arrays with the same
 *  role/name/email/employee_id/order compare equal even across a
 *  DELETE+re-INSERT round trip. Array order is preserved by JSON.stringify,
 *  which doubles as the reorder-detection signal (order_index derives from
 *  position). */
function recipientsSnapshotKey(recs: DraftRecipient[], order: EsignSigningOrder): string {
  return JSON.stringify({
    order,
    recipients: recs.map((r) => ({
      role: r.role,
      name: r.name.trim(),
      email: r.email.trim(),
      employee_id: r.employee_id,
    })),
  });
}

const STEP_LABEL: Record<WizardStep, string> = {
  1: 'Upload',
  2: 'Recipients',
  3: 'Place fields',
  4: 'Review & send',
};

export function PrepareWizardPage() {
  const { id: routeId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const confirm = useConfirm();

  // Instantiated-from-template drafts already have recipients + fields —
  // UseTemplateDialog navigates here with router state `{ startStep: 4 }` so
  // they land on the review step instead of re-walking steps 2-3. Only
  // honored when resuming an existing draft (routeId present); ignored on
  // the bare /new route, which always starts at step 1.
  const requestedStartStep = routeId ? ((location.state as any)?.startStep as WizardStep | undefined) : undefined;
  const [step, setStep] = React.useState<WizardStep>(
    requestedStartStep && requestedStartStep >= 2 && requestedStartStep <= 4
      ? requestedStartStep
      : routeId ? 2 : 1,
  );
  const [document, setDocument] = React.useState<EsignDocument | null>(null);
  const [loading, setLoading] = React.useState<boolean>(!!routeId);
  const [loadError, setLoadError] = React.useState<string | null>(null);

  const [recipients, setRecipients] = React.useState<DraftRecipient[]>([]);
  const [signingOrder, setSigningOrder] = React.useState<EsignSigningOrder>('parallel');
  const [fields, setFields] = React.useState<DraftField[]>([]);

  const [pdf, setPdf] = React.useState<PDFDocumentProxy | null>(null);
  const [pdfError, setPdfError] = React.useState<string | null>(null);

  const [employees, setEmployees] = React.useState<PickableEmployee[] | null>(null);

  const [uploading, setUploading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [sending, setSending] = React.useState(false);

  const [remindersEnabled, setRemindersEnabled] = React.useState(true);
  const [expiresInDays, setExpiresInDays] = React.useState<'' | '7' | '14' | '30'>('');

  // Content fingerprint of the last successfully-persisted recipients array
  // (+ signing order). Used to skip a needless persistRecipients() call when
  // nothing has actually changed — e.g. leaving step 3 and re-entering it via
  // step 2 (Back, then Continue again) used to unconditionally re-persist
  // recipients on every re-entry, which is a DELETE+re-INSERT on the backend
  // (replaceRecipients) and therefore cascade-deletes any fields already
  // saved against the old recipient rows, even though nothing changed.
  const lastPersistedRecipientsRef = React.useRef<string>('');

  // Load an existing draft when arriving at /dashboard/esign/:id/prepare.
  React.useEffect(() => {
    if (!routeId) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    esignApi.getDocument(routeId)
      .then((detail) => {
        if (cancelled) return;
        if (detail.document.status !== 'draft') {
          toast.error('This agreement has already been sent and can no longer be edited.');
          navigate('/dashboard/esign');
          return;
        }
        setDocument(detail.document);
        setSigningOrder(detail.document.signing_order);
        const recipientLocalIdByServerId = new Map<string, string>();
        const draftRecipients: DraftRecipient[] = detail.recipients.map((r) => {
          const localId = makeLocalId();
          recipientLocalIdByServerId.set(r.id, localId);
          return {
            localId,
            id: r.id,
            role: r.role,
            name: r.name,
            email: r.email,
            employee_id: r.employee_id,
          };
        });
        setRecipients(draftRecipients);
        lastPersistedRecipientsRef.current = recipientsSnapshotKey(draftRecipients, detail.document.signing_order);
        setFields(
          detail.fields.map((f) => ({
            localId: makeLocalId(),
            recipientLocalId: recipientLocalIdByServerId.get(f.recipient_id) ?? f.recipient_id,
            page: f.page,
            x: f.x,
            y: f.y,
            w: f.w,
            h: f.h,
            type: f.type,
            required: f.required,
          })),
        );
      })
      .catch((err: any) => setLoadError(err?.message ?? 'Failed to load this agreement'))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [routeId, navigate]);

  // Best-effort "pick from team" — hidden entirely if HR isn't reachable/permitted.
  React.useEffect(() => {
    let cancelled = false;
    import('../../hr/api')
      .then(({ hrApi }) => hrApi.listEmployees())
      .then((emps) => {
        if (cancelled) return;
        setEmployees(emps.map((e) => ({ id: e.id, name: e.name, email: e.email })));
      })
      .catch(() => { if (!cancelled) setEmployees(null); });
    return () => { cancelled = true; };
  }, []);

  // Load the PDF bytes for the field editor once we're on/past step 3.
  React.useEffect(() => {
    if (!routeId || step < 3 || pdf || pdfError) return;
    let cancelled = false;
    esignApi.downloadPdf(routeId)
      .then((objectUrl) => fetch(objectUrl).then((r) => r.arrayBuffer()))
      .then((buf) => loadPdf(buf))
      .then((doc) => { if (!cancelled) setPdf(doc); })
      .catch((err: any) => { if (!cancelled) setPdfError(err?.message ?? 'Failed to load the PDF'); });
    return () => { cancelled = true; };
  }, [routeId, step, pdf, pdfError]);

  async function handleUpload(file: File, title: string) {
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('title', title || file.name.replace(/\.pdf$/i, ''));
      const doc = await esignApi.createDocument(form);
      navigate(`/dashboard/esign/${doc.id}/prepare`);
    } catch (err: any) {
      toast.error('Upload failed', { description: err?.message });
    } finally {
      setUploading(false);
    }
  }

  /**
   * Persists the current recipients array (+ signing order) and returns a
   * fresh localId -> server-id map for THIS call, or `null` on failure.
   * Always attempts the save unconditionally — callers that want to skip a
   * needless re-persist (nothing changed) should check
   * `recipientsSnapshotKey(...) !== lastPersistedRecipientsRef.current`
   * themselves before calling this (see goToStep).
   */
  async function persistRecipients(): Promise<Map<string, string> | null> {
    if (!routeId) return null;
    // Snapshot the exact array we're sending — the request is async and the
    // user can keep editing (add/remove/reorder rows) while it's in flight.
    // Zipping the response onto whatever `recipients` looks like *later*
    // would misassign server ids to the wrong rows (or to rows that didn't
    // exist yet). Zip onto the snapshot by index instead, then apply the
    // result by localId so it's correct no matter what changed mid-flight.
    const snapshot = recipients;
    setSaving(true);
    try {
      const res = await esignApi.saveRecipients(routeId, {
        signing_order: signingOrder,
        // order_index = position in the array. The backend forces cc rows to
        // 0 regardless of what's sent, and sequential-signing turn order is
        // driven entirely by signers' order_index — sending array position
        // keeps drag/reorder (move up/down) authoritative.
        recipients: snapshot.map((r, i) => ({
          role: r.role,
          name: r.name.trim(),
          email: r.email.trim(),
          employee_id: r.employee_id,
          order_index: i,
        })),
      });
      // Backend fully replaces recipients (delete + re-insert) on every save,
      // so IDs are new each time — response order === request order,
      // guaranteed by replaceRecipients inserting in the order it received.
      const idByLocalId = new Map<string, string>();
      snapshot.forEach((r, i) => {
        const serverId = res.recipients[i]?.id;
        if (serverId) idByLocalId.set(r.localId, serverId);
      });
      setRecipients((prev) => prev.map((r) => ({ ...r, id: idByLocalId.get(r.localId) ?? r.id })));
      lastPersistedRecipientsRef.current = recipientsSnapshotKey(snapshot, signingOrder);
      return idByLocalId;
    } catch (err: any) {
      toast.error("Couldn't save recipients", { description: err?.message });
      return null;
    } finally {
      setSaving(false);
    }
  }

  /**
   * Persists the current fields array, translating each field's
   * `recipientLocalId` to a server recipient id.
   *
   * `recipientIdByLocalId`, when provided, is the map RETURNED by a
   * `persistRecipients()` call made earlier in the SAME function invocation
   * (see `handleSend`) — it must be preferred over reading `recipients` from
   * component state, because state updates from `setRecipients` inside
   * `persistRecipients` don't retroactively mutate the `recipients` binding
   * this closure captured at render time; without threading the map through
   * explicitly, `persistFields` would keep using the pre-persist (possibly
   * already-deleted, per the backend's delete+re-insert semantics) recipient
   * ids. Falls back to the current `recipients` state for callers that
   * aren't chaining directly off a `persistRecipients()` result (e.g. the
   * step 3 -> 4 transition, which runs well after any earlier persist has
   * already flowed through a render).
   */
  async function persistFields(recipientIdByLocalId?: Map<string, string>): Promise<boolean> {
    if (!routeId) return false;
    function idFor(localId: string): string | undefined {
      return recipientIdByLocalId?.get(localId) ?? recipients.find((r) => r.localId === localId)?.id ?? undefined;
    }
    setSaving(true);
    try {
      await esignApi.saveFields(routeId, {
        fields: fields.map((f) => ({
          recipient_id: idFor(f.recipientLocalId) ?? f.recipientLocalId,
          page: f.page,
          x: f.x,
          y: f.y,
          w: f.w,
          h: f.h,
          type: f.type,
          required: f.required,
        })),
      });
      return true;
    } catch (err: any) {
      toast.error("Couldn't save fields", { description: err?.message });
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function handleRoleChangeRequest(localId: string, nextRole: EsignRecipientRole) {
    const recipient = recipients.find((r) => r.localId === localId);
    if (!recipient || recipient.role === nextRole) return;
    if (nextRole === 'cc') {
      const affected = fields.filter((f) => f.recipientLocalId === localId);
      if (affected.length > 0) {
        const ok = await confirm({
          title: 'Switch to CC?',
          description: `${recipient.name || 'This recipient'} has ${affected.length} field${affected.length === 1 ? '' : 's'} placed. Switching to CC (view only) will remove them.`,
          confirmLabel: 'Switch to CC',
          destructive: true,
        });
        if (!ok) return;
        setFields((prev) => prev.filter((f) => f.recipientLocalId !== localId));
      }
    }
    setRecipients((prev) => prev.map((r) => (r.localId === localId ? { ...r, role: nextRole } : r)));
  }

  async function handleRemoveRequest(localId: string) {
    const recipient = recipients.find((r) => r.localId === localId);
    if (!recipient) return;
    const affected = fields.filter((f) => f.recipientLocalId === localId);
    if (affected.length > 0) {
      const ok = await confirm({
        title: 'Remove recipient?',
        description: `${recipient.name || 'This recipient'} has ${affected.length} field${affected.length === 1 ? '' : 's'} placed. Removing them will also remove those fields.`,
        confirmLabel: 'Remove',
        destructive: true,
      });
      if (!ok) return;
      setFields((prev) => prev.filter((f) => f.recipientLocalId !== localId));
    }
    setRecipients((prev) => prev.filter((r) => r.localId !== localId));
  }

  const canAdvanceToFields = recipientsAreValid(recipients);
  const canSend = everySignerHasSignatureField(recipients, fields);

  async function goToStep(next: WizardStep) {
    if (step === 2 && next > 2) {
      if (!canAdvanceToFields) {
        toast.error('Add at least one signer with a valid name and email before continuing.');
        return;
      }
      // Dirty-tracking: only re-persist recipients if something actually
      // changed since the last successful save. Re-persisting unconditionally
      // on every step-2->3 visit (e.g. Back then Continue again, with zero
      // edits) used to DELETE+re-INSERT recipients on the backend every time,
      // which cascade-deletes any fields already saved against the old
      // recipient rows — silently wiping placements on step re-entry.
      const dirty = recipientsSnapshotKey(recipients, signingOrder) !== lastPersistedRecipientsRef.current;
      if (dirty) {
        const map = await persistRecipients();
        if (!map) return;
      }
    }
    if (step === 3 && next > 3) {
      const ok = await persistFields();
      if (!ok) return;
    }
    setStep(next);
  }

  async function handleSend() {
    if (!routeId) return;
    if (!canSend) {
      toast.error('Every signer needs at least one signature field before you can send.');
      return;
    }
    setSending(true);
    try {
      // Contract: both recipients and fields must be persisted before send —
      // not just fields. Recipients first (fields reference recipient ids).
      // Unlike goToStep's step-2->3 transition, this always re-persists
      // recipients unconditionally — belt-and-suspenders right before the
      // point of no return. persistRecipients's return value (the fresh
      // localId->serverId map for THIS call) is threaded directly into
      // persistFields rather than relying on `recipients` component state:
      // no render happens between these two awaits (they're both inside the
      // same closure from the same render), so `recipients` here is still
      // the pre-persist snapshot — using it instead of the map would target
      // recipient rows the backend just deleted.
      const idByLocalId = await persistRecipients();
      if (!idByLocalId) return;
      const fieldsOk = await persistFields(idByLocalId);
      if (!fieldsOk) return;
      const result = await esignApi.send(routeId, {
        reminders_enabled: remindersEnabled,
        ...(expiresInDays ? { expires_in_days: Number(expiresInDays) as 7 | 14 | 30 } : {}),
      });
      toast.success('Sent for signature');
      if (result?.self_sign_url) {
        // Full navigation (not react-router) — the self-sign URL is the
        // public /sign/:token route, a wholly different app surface.
        window.location.assign(result.self_sign_url);
      } else {
        navigate(`/dashboard/esign/${routeId}`);
      }
    } catch (err: any) {
      toast.error('Failed to send', { description: err?.message });
    } finally {
      setSending(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-ink/40 dark:text-white/40" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen">
        <PageHeader eyebrow="Money · E-Sign" title="Prepare agreement" />
        <div className="px-6 sm:px-8 lg:px-10 pb-12">
          <p className="text-[14px] text-rose-600 dark:text-rose-400">{loadError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <PageHeader
        eyebrow="Money · E-Sign"
        title={document?.title ?? 'Prepare agreement'}
        description={`Step ${step} of 4 — ${STEP_LABEL[step]}`}
      />

      <div className="px-6 sm:px-8 lg:px-10 pb-16 space-y-6">
        <ol className="flex items-center gap-2 text-[12.5px]">
          {([1, 2, 3, 4] as WizardStep[]).map((s) => (
            <li
              key={s}
              className={cn(
                'rounded-full px-3 py-1 font-medium',
                s === step
                  ? 'bg-coral text-white'
                  : s < step
                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
                    : 'bg-ink/5 dark:bg-white/10 text-ink/50 dark:text-white/50',
              )}
            >
              {s}. {STEP_LABEL[s]}
            </li>
          ))}
        </ol>

        {step === 1 && <UploadStep uploading={uploading} onUpload={handleUpload} />}

        {step === 2 && (
          <div className="space-y-6">
            <RecipientsEditor
              recipients={recipients}
              onRecipientsChange={setRecipients}
              signingOrder={signingOrder}
              onSigningOrderChange={setSigningOrder}
              onRoleChangeRequest={handleRoleChangeRequest}
              onRemoveRequest={handleRemoveRequest}
              employees={employees}
              disabled={saving}
            />
            <div className="flex justify-end">
              <Button disabled={!canAdvanceToFields || saving} onClick={() => goToStep(3)}>
                {saving ? 'Saving…' : 'Continue to field placement'}
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6">
            {pdfError && <p className="text-[13px] text-rose-600 dark:text-rose-400">{pdfError}</p>}
            {!pdf && !pdfError && (
              <div className="flex items-center gap-2 text-[13px] text-ink/50 dark:text-white/50">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading document…
              </div>
            )}
            {pdf && document && (
              <FieldEditor
                pdf={pdf}
                pageCount={document.page_count}
                recipients={recipients}
                fields={fields}
                onFieldsChange={setFields}
              />
            )}
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
              <Button disabled={saving} onClick={() => goToStep(4)}>
                {saving ? 'Saving…' : 'Continue to review'}
              </Button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-6">
            <div className="rounded-2xl bg-white/70 dark:bg-white/[0.04] ring-1 ring-ink/8 dark:ring-white/10 p-5 space-y-3">
              <h3 className="text-[14px] font-semibold text-ink dark:text-white">Summary</h3>
              <p className="text-[13.5px] text-ink/70 dark:text-white/70">
                {recipients.filter((r) => r.role === 'signer').length} signer(s),{' '}
                {recipients.filter((r) => r.role === 'cc').length} cc, {fields.length} field(s) placed —{' '}
                {signingOrder === 'sequential' ? 'signing in order' : 'signing in any order'}.
              </p>
              <ul className="space-y-1">
                {recipients.map((r) => (
                  <li key={r.localId} className="text-[13px] text-ink/60 dark:text-white/60">
                    {r.name} ({r.email}) — {r.role === 'signer' ? 'Signer' : 'CC'}
                    {r.role === 'signer' && !fields.some((f) => f.recipientLocalId === r.localId && f.type === 'signature') && (
                      <span className="ml-2 text-rose-600 dark:text-rose-400">missing signature field</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl bg-white/70 dark:bg-white/[0.04] ring-1 ring-ink/8 dark:ring-white/10 p-5 space-y-4">
              <h3 className="text-[14px] font-semibold text-ink dark:text-white">Send options</h3>
              <label className="flex items-center gap-2.5 text-[13.5px] text-ink/75 dark:text-white/75">
                <input
                  type="checkbox"
                  role="checkbox"
                  checked={remindersEnabled}
                  onChange={(e) => setRemindersEnabled(e.target.checked)}
                  aria-label="Remind signers automatically"
                />
                Remind signers automatically
              </label>
              <label className="block max-w-xs">
                <span className="text-[13px] font-semibold text-ink/70 dark:text-white/70">Link expiry</span>
                <select
                  aria-label="Link expiry"
                  className="mt-1.5 w-full rounded-xl border border-ink/15 dark:border-white/15 bg-white dark:bg-ink-900 px-3 py-2 text-[13.5px] text-ink dark:text-white"
                  value={expiresInDays}
                  onChange={(e) => setExpiresInDays(e.target.value as '' | '7' | '14' | '30')}
                >
                  <option value="">None</option>
                  <option value="7">7 days</option>
                  <option value="14">14 days</option>
                  <option value="30">30 days</option>
                </select>
              </label>
            </div>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(3)}>Back</Button>
              <Button disabled={!canSend || sending} onClick={handleSend}>
                {sending ? 'Sending…' : 'Send for signature'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function UploadStep({ uploading, onUpload }: { uploading: boolean; onUpload: (file: File, title: string) => void }) {
  const [file, setFile] = React.useState<File | null>(null);
  const [title, setTitle] = React.useState('');

  return (
    <div className="max-w-lg space-y-4 rounded-2xl bg-white/70 dark:bg-white/[0.04] ring-1 ring-ink/8 dark:ring-white/10 p-6">
      <label className="block">
        <span className="text-[13px] font-semibold text-ink/70 dark:text-white/70">Title</span>
        <Input
          className="mt-1.5"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. NDA — Acme Co"
        />
      </label>

      <label className="flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-ink/15 dark:border-white/15 p-8 cursor-pointer text-center">
        <UploadCloud className="h-6 w-6 text-ink/40 dark:text-white/40" />
        <span className="text-[13.5px] text-ink/70 dark:text-white/70">
          {file ? file.name : 'Choose a PDF to upload'}
        </span>
        <input
          type="file"
          accept="application/pdf"
          className="hidden"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
      </label>

      <div className="flex justify-end">
        <Button
          disabled={!file || uploading}
          onClick={() => file && onUpload(file, title)}
        >
          {uploading ? 'Uploading…' : 'Continue'}
        </Button>
      </div>
    </div>
  );
}
